import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { LogOut, RefreshCw, Inbox, Lock } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

const STATUS_STYLES = {
  new: "bg-gold-light text-gold-hover",
  reviewed: "bg-sand text-stone2",
  contacted: "bg-navy-light/10 text-navy-light",
  archived: "bg-line text-mist",
};

const FILTERS = ["all", "new", "reviewed", "contacted", "archived"];

function Login({ onLogin }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      await axios.post(
        `${API}/api/admin/login`,
        { email, password },
        { withCredentials: true },
      );
      onLogin();
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-ivory flex items-center justify-center px-5">
      <form
        onSubmit={submit}
        className="w-full max-w-sm bg-white border border-line rounded-md p-9"
      >
        <span className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-navy">
          <Lock className="w-5 h-5 text-gold" />
        </span>
        <h1 className="mt-6 font-serif text-2xl text-navy">
          Private review area
        </h1>
        <p className="mt-2 text-sm text-stone2">
          Booking inquiries are reviewed here. Please sign in.
        </p>
        <div className="mt-7 space-y-5">
          <div>
            <label htmlFor="admin-email" className="block text-sm font-medium text-navy">
              Email
            </label>
            <input
              id="admin-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              data-testid="admin-login-email"
              className="mt-2 w-full border border-line-strong rounded-md px-4 py-3 text-[15px] focus:border-gold focus:ring-1 focus:ring-gold outline-none"
            />
          </div>
          <div>
            <label htmlFor="admin-password" className="block text-sm font-medium text-navy">
              Password
            </label>
            <input
              id="admin-password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              data-testid="admin-login-password"
              className="mt-2 w-full border border-line-strong rounded-md px-4 py-3 text-[15px] focus:border-gold focus:ring-1 focus:ring-gold outline-none"
            />
          </div>
          {error && (
            <p className="text-sm text-burgundy" role="alert" data-testid="admin-login-error">
              {error}
            </p>
          )}
          <button
            type="submit"
            disabled={busy}
            data-testid="admin-login-submit"
            className="w-full bg-navy text-ivory py-3.5 rounded-full text-sm font-medium hover:bg-navy-light transition-colors disabled:opacity-60"
          >
            {busy ? "Signing in…" : "Sign in"}
          </button>
        </div>
      </form>
    </div>
  );
}

function ChangePasswordCard() {
  const [open, setOpen] = useState(false);
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [msg, setMsg] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setMsg("");
    if (next !== confirm) {
      setErr("The two new passwords don't match.");
      return;
    }
    if (next.length < 10) {
      setErr("New password must be at least 10 characters.");
      return;
    }
    setBusy(true);
    try {
      await axios.post(
        `${API}/api/admin/change-password`,
        { currentPassword: current, newPassword: next },
        { withCredentials: true },
      );
      setMsg("Password updated. Use your new password next time you sign in.");
      setOpen(false);
      setCurrent("");
      setNext("");
      setConfirm("");
    } catch (err2) {
      setErr(formatApiErrorDetail(err2.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="w-full sm:w-auto">
      <button
        onClick={() => {
          setOpen(!open);
          setMsg("");
          setErr("");
        }}
        data-testid="admin-change-password-toggle"
        aria-expanded={open}
        className="inline-flex items-center gap-2 border border-line-strong text-stone2 px-4 py-2.5 rounded-full text-sm hover:border-gold hover:text-gold-hover transition-colors"
      >
        <Lock className="w-4 h-4" /> Change password
      </button>
      {open && (
        <form
          onSubmit={submit}
          className="mt-4 bg-white border border-line rounded-md p-6 space-y-4 sm:max-w-md"
        >
          <p className="text-sm text-stone2 leading-relaxed">
            Choose your own password. It is stored only as a bcrypt hash —
            never as readable text.
          </p>
          <input
            type="password"
            value={current}
            onChange={(e) => setCurrent(e.target.value)}
            placeholder="Current password"
            data-testid="admin-input-current-password"
            autoComplete="current-password"
            className="w-full border border-line-strong rounded-md px-4 py-2.5 text-sm focus:border-gold focus:ring-1 focus:ring-gold outline-none"
          />
          <input
            type="password"
            value={next}
            onChange={(e) => setNext(e.target.value)}
            placeholder="New password (at least 10 characters)"
            data-testid="admin-input-new-password"
            autoComplete="new-password"
            className="w-full border border-line-strong rounded-md px-4 py-2.5 text-sm focus:border-gold focus:ring-1 focus:ring-gold outline-none"
          />
          <input
            type="password"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            placeholder="Repeat the new password"
            data-testid="admin-input-confirm-password"
            autoComplete="new-password"
            className="w-full border border-line-strong rounded-md px-4 py-2.5 text-sm focus:border-gold focus:ring-1 focus:ring-gold outline-none"
          />
          {err && (
            <p className="text-xs text-burgundy" role="alert">
              {err}
            </p>
          )}
          {msg && (
            <p
              className="text-xs text-[#2D6A4F]"
              data-testid="admin-change-password-success"
              role="status"
            >
              {msg}
            </p>
          )}
          <button
            type="submit"
            disabled={busy}
            data-testid="admin-change-password-submit"
            className="w-full bg-navy text-ivory py-3 rounded-full text-sm font-medium hover:bg-navy-light transition-colors disabled:opacity-60 disabled:cursor-wait"
          >
            {busy ? "Updating…" : "Set the new password"}
          </button>
        </form>
      )}
    </div>
  );
}

export default function Admin() {
  const [checking, setChecking] = useState(true);
  const [authed, setAuthed] = useState(false);
  const [inquiries, setInquiries] = useState([]);
  const [filter, setFilter] = useState("all");
  const [typeFilter, setTypeFilter] = useState("all");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      let res;
      try {
        res = await axios.get(`${API}/api/admin/inquiries`, {
          withCredentials: true,
        });
      } catch (err) {
        if (err.response?.status === 401) {
          const refresh = await axios.post(
            `${API}/api/admin/refresh`,
            {},
            { withCredentials: true },
          );
          if (refresh.data && refresh.data.ok) {
            res = await axios.get(`${API}/api/admin/inquiries`, {
              withCredentials: true,
            });
          } else {
            throw err;
          }
        } else {
          throw err;
        }
      }
      setInquiries(res.data || []);
      setAuthed(true);
    } catch (err) {
      if (err.response?.status === 401) {
        setAuthed(false);
      } else {
        setError(formatApiErrorDetail(err.response?.data?.detail));
      }
    } finally {
      setLoading(false);
      setChecking(false);
    }
  }, []);

  useEffect(() => {
    axios
      .get(`${API}/api/admin/me`, { withCredentials: true })
      .then(() => {
        setAuthed(true);
        load();
      })
      .catch(() => {
        setChecking(false);
        setAuthed(false);
      });
  }, [load]);

  const logout = async () => {
    await axios.post(`${API}/api/admin/logout`, {}, { withCredentials: true });
    setAuthed(false);
  };

  const setStatus = async (id, status) => {
    try {
      const res = await axios.patch(
        `${API}/api/admin/inquiries/${id}/status`,
        { status },
        { withCredentials: true },
      );
      setInquiries((prev) =>
        prev.map((q) => (q.id === id ? { ...q, status: res.data.status } : q)),
      );
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  if (checking) {
    return (
      <div className="min-h-screen bg-ivory flex items-center justify-center">
        <p className="text-mist text-sm">Checking your session…</p>
      </div>
    );
  }

  if (!authed) return <Login onLogin={load} />;

  const shown = inquiries
    .filter((q) => (filter === "all" ? true : q.status === filter))
    .filter((q) =>
      typeFilter === "all" ? true : (q.kind || "speaking") === typeFilter,
    );
  const countFor = (f) =>
    f === "all"
      ? inquiries.length
      : inquiries.filter((q) => q.status === f).length;

  return (
    <div className="min-h-screen bg-ivory">
      <div className="mx-auto max-w-5xl px-5 sm:px-8 py-12 sm:py-16">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <h1 className="font-serif text-3xl text-navy">Booking inquiries</h1>
            <p className="mt-1 text-sm text-mist">
              Private — visible only here, never on the public site.
            </p>
          </div>
          <div className="flex flex-col items-end gap-3">
            <div className="flex items-center gap-3">
              <button
                onClick={load}
                data-testid="admin-refresh-button"
                className="inline-flex items-center gap-2 border border-line-strong text-stone2 px-4 py-2.5 rounded-full text-sm hover:border-gold hover:text-gold-hover transition-colors"
              >
                <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
                Refresh
              </button>
              <button
                onClick={logout}
                data-testid="admin-logout-button"
                className="inline-flex items-center gap-2 bg-navy text-ivory px-4 py-2.5 rounded-full text-sm hover:bg-navy-light transition-colors"
              >
                <LogOut className="w-4 h-4" /> Sign out
              </button>
            </div>
            <ChangePasswordCard />
          </div>
        </div>

        <div className="mt-8 flex flex-wrap gap-2">
          {[
            { v: "all", l: "All types" },
            { v: "speaking", l: "Speaking & signings" },
            { v: "bulk", l: "Group copies" },
            { v: "ask", l: "Ask Chaplain Derrick" },
          ].map((t) => (
            <button
              key={t.v}
              onClick={() => setTypeFilter(t.v)}
              data-testid={`admin-typefilter-${t.v}`}
              className={`text-sm px-4 py-2 rounded-full border transition-colors ${
                typeFilter === t.v
                  ? "bg-burgundy text-ivory border-burgundy"
                  : "bg-white text-stone2 border-line-strong hover:border-gold"
              }`}
            >
              {t.l}
            </button>
          ))}
        </div>

        <div className="mt-3 flex flex-wrap gap-2">
          {FILTERS.map((f) => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              data-testid={`admin-filter-${f}`}
              className={`text-sm px-4 py-2 rounded-full border transition-colors ${
                filter === f
                  ? "bg-navy text-ivory border-navy"
                  : "bg-white text-stone2 border-line-strong hover:border-gold"
              }`}
            >
              {f.charAt(0).toUpperCase() + f.slice(1)} ({countFor(f)})
            </button>
          ))}
        </div>

        {error && (
          <p className="mt-6 text-sm text-burgundy bg-burgundy-soft border border-burgundy/20 rounded-md px-4 py-3" role="alert">
            {error}
          </p>
        )}

        <div className="mt-8 space-y-4" data-testid="admin-inquiries-list">
          {shown.length === 0 && !loading && (
            <div className="bg-white border border-line rounded-md p-12 text-center">
              <Inbox className="w-8 h-8 text-mist mx-auto" />
              <p className="mt-4 text-stone2">
                No {filter === "all" ? "inquiries" : filter + " inquiries"} yet.
              </p>
            </div>
          )}

          {shown.map((q) => (
            <div
              key={q.id}
              className="bg-white border border-line rounded-md p-6 sm:p-7"
              data-testid={`admin-inquiry-card-${q.id}`}
            >
              <div className="flex flex-wrap items-start justify-between gap-4">
                <div>
                  <p className="font-serif text-lg text-navy">
                    {q.fullName || q.contactName}
                  </p>
                  <p className="text-sm text-stone2">
                    {q.email}
                    {q.organization ? ` · ${q.organization}` : ""}
                  </p>
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-[10px] uppercase tracking-[0.16em] px-3 py-1 rounded-full bg-burgundy-soft text-burgundy">
                    {(q.kind || "speaking") === "bulk"
                      ? "Group copies"
                      : (q.kind || "speaking") === "ask"
                      ? "Ask Chaplain Derrick"
                      : "Speaking"}
                  </span>
                  <span
                    className={`text-[10px] uppercase tracking-[0.16em] px-3 py-1 rounded-full ${
                      STATUS_STYLES[q.status] || STATUS_STYLES.new
                    }`}
                  >
                    {q.status}
                  </span>
                  <select
                    value={q.status}
                    onChange={(e) => setStatus(q.id, e.target.value)}
                    data-testid={`admin-status-select-${q.id}`}
                    aria-label={`Change status for ${q.fullName}`}
                    className="text-xs border border-line-strong rounded-md px-3 py-2 bg-white text-stone2 focus:border-gold outline-none"
                  >
                    <option value="new">New</option>
                    <option value="reviewed">Reviewed</option>
                    <option value="contacted">Contacted</option>
                    <option value="archived">Archived</option>
                  </select>
                </div>
              </div>

              {(q.kind || "speaking") === "bulk" ? (
                <dl className="mt-5 grid sm:grid-cols-3 gap-4 text-sm">
                  <div>
                    <dt className="text-[11px] uppercase tracking-[0.14em] text-mist">
                      Titles
                    </dt>
                    <dd className="mt-1 text-ink">{q.titles}</dd>
                  </div>
                  <div>
                    <dt className="text-[11px] uppercase tracking-[0.14em] text-mist">
                      Estimated quantity
                    </dt>
                    <dd className="mt-1 text-ink">{q.estimatedQuantity}</dd>
                  </div>
                  <div>
                    <dt className="text-[11px] uppercase tracking-[0.14em] text-mist">
                      Delivery location
                    </dt>
                    <dd className="mt-1 text-ink">{q.deliveryLocation}</dd>
                  </div>
                  <div>
                    <dt className="text-[11px] uppercase tracking-[0.14em] text-mist">
                      Timeframe
                    </dt>
                    <dd className="mt-1 text-ink">{q.timeframe}</dd>
                  </div>
                  <div>
                    <dt className="text-[11px] uppercase tracking-[0.14em] text-mist">
                      Phone
                    </dt>
                    <dd className="mt-1 text-ink">{q.phone || "—"}</dd>
                  </div>
                </dl>
              ) : (q.kind || "speaking") === "ask" ? (
                <dl className="mt-5 grid sm:grid-cols-2 gap-4 text-sm">
                  <div>
                    <dt className="text-[11px] uppercase tracking-[0.14em] text-mist">
                      Inquiry about
                    </dt>
                    <dd
                      className="mt-1 text-ink"
                      data-testid={`admin-ask-category-${q.id}`}
                    >
                      {q.category || "General Question"}
                    </dd>
                  </div>
                  {q.source ? (
                    <div>
                      <dt className="text-[11px] uppercase tracking-[0.14em] text-mist">
                        Source
                      </dt>
                      <dd
                        className="mt-1 text-ink"
                        data-testid={`admin-ask-source-${q.id}`}
                      >
                        {q.source}
                      </dd>
                    </div>
                  ) : null}
                  {q.pageUrl ? (
                    <div className="sm:col-span-2">
                      <dt className="text-[11px] uppercase tracking-[0.14em] text-mist">
                        Page URL
                      </dt>
                      <dd
                        className="mt-1 text-ink break-all"
                        data-testid={`admin-ask-page-url-${q.id}`}
                      >
                        {q.pageUrl}
                      </dd>
                    </div>
                  ) : null}
                </dl>
              ) : (
                <dl className="mt-5 grid sm:grid-cols-3 gap-4 text-sm">
                  <div>
                    <dt className="text-[11px] uppercase tracking-[0.14em] text-mist">
                      Type of request
                    </dt>
                    <dd className="mt-1 text-ink">{q.requestType}</dd>
                  </div>
                  <div>
                    <dt className="text-[11px] uppercase tracking-[0.14em] text-mist">
                      Preferred date
                    </dt>
                    <dd className="mt-1 text-ink">{q.preferredDate || "—"}</dd>
                  </div>
                  <div>
                    <dt className="text-[11px] uppercase tracking-[0.14em] text-mist">
                      Format
                    </dt>
                    <dd className="mt-1 text-ink">{q.locationFormat}</dd>
                  </div>
                </dl>
              )}

              <div className="mt-5 border-t border-line pt-4">
                <p className="text-[11px] uppercase tracking-[0.14em] text-mist">
                  {(q.kind || "speaking") === "bulk"
                    ? "Message"
                    : (q.kind || "speaking") === "ask"
                    ? "Question"
                    : "Event details"}
                </p>
                <p className="mt-2 text-sm text-stone2 whitespace-pre-wrap leading-relaxed">
                  {q.eventDetails || q.message || q.question || "—"}
                </p>
              </div>

              <p className="mt-4 text-[11px] text-mist">
                Received{" "}
                {new Date(q.createdAt).toLocaleString(undefined, {
                  dateStyle: "medium",
                  timeStyle: "short",
                })}
              </p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
